import React from "react";
import { LinearProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { refreshProgressPercent, refreshStageText } from "./refreshProgress";

/*
 * The dashboard Refresh progress banner (landingscreen/refreshProgress.js).
 *
 * state: null (hidden) or
 *   { phase: "running", job }          stage text + determinate bar (day i of n)
 *   { phase: "legacy" }                the old single call: indeterminate bar,
 *                                      "this can take a few minutes"
 *   { phase: "busy", text }            one quick call (7825 appointments/sync):
 *                                      indeterminate bar
 *   { phase: "done", job }             the counts; the dashboard reloads
 *   { phase: "error", message }        message + Try again
 * Closing it never stops the refresh: the server job keeps running and the
 * result arrives as a toast.
 */
export default function RefreshProgressBanner({ state, onClose, onRetry }) {
  if (!state) return null;
  const { phase, job } = state;
  const running = phase === "running" || phase === "legacy" || phase === "busy";
  let text;
  if (phase === "legacy") text = "Refreshing appointments… this can take a few minutes.";
  else if (phase === "busy") text = state.text || "Checking Athena…";
  else if (phase === "error") text = state.message || "The refresh could not finish. Please try again.";
  else text = refreshStageText(job);
  const percent = phase === "running" ? refreshProgressPercent(job) : 100;
  const tone =
    phase === "error"
      ? "border-[#FCA5A5] bg-[#FEF2F2] text-[#991B1B]"
      : phase === "done"
        ? "border-[#86EFAC] bg-[#F0FDF4] text-[#166534]"
        : "border-[#BFDBFE] bg-[#EFF6FF] text-[#1E3A8A]";

  return (
    <div
      className={`refresh-progress-banner absolute right-0 top-full z-[1200] mt-2 w-[340px] max-w-[calc(100vw-32px)] rounded-[8px] border px-3 py-2 shadow-[0_4px_16px_rgba(15,23,42,0.12)] ${tone}`}
      data-phase={phase}
    >
      <div className="flex items-start gap-2">
        <div
          className="flex-1 text-[13px] font-medium leading-snug"
          role={phase === "error" ? "alert" : "status"}
          aria-live={phase === "error" ? "assertive" : "polite"}
          aria-atomic="true"
        >
          {text}
        </div>
        <button
          type="button"
          className="refresh-progress-close -mr-1 rounded p-[2px] leading-none opacity-70 hover:opacity-100"
          aria-label={running ? "Hide refresh progress (the refresh keeps running)" : "Close"}
          title={running ? "Hide (the refresh keeps running)" : "Close"}
          onClick={onClose}
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>
      {running && (
        <LinearProgress
          className="mt-2"
          variant={phase === "running" ? "determinate" : "indeterminate"}
          value={phase === "running" ? percent : undefined}
          aria-label="Refresh progress"
          sx={{ height: 6, borderRadius: 3 }}
        />
      )}
      {phase === "error" && onRetry && (
        <button
          type="button"
          className="refresh-progress-retry mt-2 rounded-[6px] border border-current px-2 py-[2px] text-[12px] font-semibold"
          onClick={onRetry}
        >
          Try again
        </button>
      )}
    </div>
  );
}
